import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import { readEnvironmentApi } from "../environmentApi";
import { newCommandId } from "../lib/utils";
import { toastManager } from "../components/ui/toast";

/**
 * Commits a thread rename, wherever the rename was started from.
 *
 * Lifted out of `useSidebarThreadHandlers` when the breadcrumb gained an
 * inline rename of its own. The rules here are the ones a second
 * implementation would get subtly wrong: an empty title is refused rather
 * than written, an unchanged title is a no-op rather than a dispatch, and the
 * editing state is closed on **every** path including failure — a rename box
 * that stays open after an error reads as the keystrokes having been lost.
 *
 * @module commitThreadRename
 */

export async function commitThreadRename(input: {
  readonly threadRef: ScopedThreadRef;
  readonly newTitle: string;
  readonly originalTitle: string;
  /** Closes the editor. Called exactly once, on every path. */
  readonly finishRename: () => void;
}): Promise<void> {
  const { threadRef, newTitle, originalTitle, finishRename } = input;

  const trimmed = newTitle.trim();
  if (trimmed.length === 0) {
    toastManager.add({ type: "warning", title: "Thread title cannot be empty" });
    finishRename();
    return;
  }
  if (trimmed === originalTitle) {
    finishRename();
    return;
  }
  const api = readEnvironmentApi(threadRef.environmentId);
  if (!api) {
    toastManager.add({
      type: "warning",
      title: "Connect to this environment to rename the thread",
    });
    finishRename();
    return;
  }
  try {
    await api.orchestration.dispatchCommand({
      type: "thread.meta.update",
      commandId: newCommandId(),
      threadId: threadRef.threadId,
      title: trimmed,
    });
  } catch (error) {
    toastManager.add({
      type: "error",
      title: "Failed to rename thread",
      description: error instanceof Error ? error.message : "An error occurred.",
    });
  }
  finishRename();
}
